<script setup>
import { computed } from 'vue'

import DayChart from '@/components/dashboards/DayChart.vue'

const props = defineProps({
  report: { type: Object, required: true },
})

// Validated for colour-blind readers and for contrast on the light surface
const COLOR = {
  ruby: '#e54666',
  amber: '#ab6400',
  violet: '#654dc4',
  blue: '#0588f0',
  teal: '#008573',
}
// Each loss keeps its colour wherever it shows: a tile, a bar, a chart
const KINDS = [
  { key: 'unanswered', color: COLOR.ruby },
  { key: 'stale_requeued', color: COLOR.amber },
  { key: 'bot_stalled', color: COLOR.violet },
  { key: 'failed_messages', color: COLOR.blue },
  { key: 'vk_recovered', color: COLOR.teal },
  { key: 'booking_failed', color: COLOR.amber },
]

const totals = computed(() => props.report.totals)
const days = computed(() => props.report.days)
const kinds = computed(() =>
  KINDS.map(k => ({ ...k, value: totals.value[k.key] ?? 0 }))
)
const kindsMax = computed(() => Math.max(1, ...kinds.value.map(k => k.value)))
const unansweredPoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.unanswered }))
)
const stalePoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.stale_requeued }))
)
const fmt = n => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n))
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.UNANSWERED')
        }}</span>
        <span class="text-2xl font-semibold text-n-ruby-11">{{
          fmt(totals.unanswered)
        }}</span>
        <span class="text-xs text-n-slate-11">
          {{
            $t('DASHBOARDS.LOSSES.UNANSWERED_SUB', {
              share: totals.unanswered_share ?? '—',
            })
          }}
        </span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.STALE')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.stale_requeued)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.STALE_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.BOT_STALLED')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.bot_stalled)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.BOT_STALLED_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.FAILED')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.failed_messages)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.FAILED_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.VK')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.vk_recovered)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.VK_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.BOOKING')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.booking_failed)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOSSES.BOOKING_SUB')
        }}</span>
      </div>
    </div>

    <section
      class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
    >
      <h2 class="m-0 text-heading-3 text-n-slate-12">
        {{ $t('DASHBOARDS.LOSSES.WHERE') }}
      </h2>
      <div v-for="k in kinds" :key="k.key" class="flex items-center gap-3">
        <span class="w-56 text-sm text-n-slate-12 shrink-0">{{
          $t(`DASHBOARDS.LOSSES.KIND.${k.key}`)
        }}</span>
        <div class="flex-1 h-5 rounded bg-n-alpha-1">
          <div
            class="h-5 rounded"
            :style="{
              width: `${(k.value * 100) / kindsMax}%`,
              background: k.color,
            }"
          />
        </div>
        <span
          class="w-12 text-sm text-right tabular-nums text-n-slate-12 shrink-0"
        >
          {{ fmt(k.value) }}
        </span>
      </div>
      <span class="text-xs text-n-slate-11">
        {{
          $t('DASHBOARDS.LOSSES.OVERNIGHT', {
            count: fmt(totals.waited_overnight),
          })
        }}
      </span>
    </section>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOSSES.UNANSWERED_BY_DAY') }}
        </h2>
        <DayChart :points="unansweredPoints" kind="bars" :color="COLOR.ruby" />
      </section>
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOSSES.STALE_BY_DAY') }}
        </h2>
        <DayChart :points="stalePoints" kind="bars" :color="COLOR.amber" />
      </section>
    </div>
  </div>
</template>
